// Orchestration du lot d'import (NutriScan 10, § « Sélection multiple et file de traitement ») :
// compression -> upload -> analyse, UNE photo après l'autre, jamais en parallèle — le garde-fou
// budgétaire et la limitation de débit (api/src/budgetGuard.ts, api/src/rateLimit.ts) s'appliquent
// par appel, un lot en parallèle les mettrait en défaut instantanément. Un échec sur une photo ne
// doit jamais faire perdre les résultats déjà obtenus (critère d'acceptation) : chaque photo est
// traitée dans son propre bloc `try/catch`, la boucle continue après un échec.
//
// Tout ce qui touche réseau/DOM est INJECTÉ (`DependancesImport`, même parti pris que
// `capture/compression.ts` `DependancesCompression`) : cette boucle est donc testable sous
// `node --test`, sans navigateur (test/importRunner.test.ts).
import type { PhotoCompressee } from '../capture/compression.js';
import { estProbablementHeic } from '../capture/heic.js';
import type { AlimentDetecte, AnalyzeDone } from '../analyze/types.js';
import type { PhotoImportee, ResultatImportPhoto } from './types.js';

export interface EcouteursAnalyseImport {
  surAliment(aliment: AlimentDetecte): void;
  surReprise?(): void;
}

export interface DependancesImport {
  /** Octets de tête du fichier (détection HEIC) — quelques dizaines d'octets suffisent. */
  lireEntete(fichier: File): Promise<Uint8Array>;
  compresser(fichier: File): Promise<PhotoCompressee>;
  televerser(blob: Blob, options: { signal?: AbortSignal }): Promise<string>;
  analyser(blobId: string, ecouteurs: EcouteursAnalyseImport, options: { signal?: AbortSignal }): Promise<AnalyzeDone>;
}

const MESSAGE_HEIC =
  "Ce format (HEIC/HEIF) n'est pas lisible par ce navigateur : réessaie depuis ton téléphone, ou convertis la photo en JPEG.";
const MESSAGE_LECTURE_IMPOSSIBLE = "Cette image n'a pas pu être lue — fichier corrompu ou format non pris en charge.";
const MESSAGE_ENVOI_ECHEC = "L'envoi de la photo a échoué.";
const MESSAGE_ANALYSE_ECHEC = "L'analyse de cette photo a échoué.";
const MESSAGE_ANNULE = 'Import annulé.';

function estAbandon(err: unknown, signal: AbortSignal | undefined): boolean {
  return Boolean(signal?.aborted) || (err instanceof DOMException && err.name === 'AbortError');
}

/**
 * Traite les photos EN SÉRIE. Rend le tableau complet des résultats (un par photo présentée, y
 * compris celles déjà traitées avant une annulation) — jamais une exception : un échec est un
 * `ResultatImportPhoto` de statut `'echec'`, pas un rejet qui ferait perdre le reste du lot.
 */
export async function traiterImportsEnSerie(
  photos: readonly PhotoImportee[],
  deps: DependancesImport,
  surProgres: (resultat: ResultatImportPhoto, index: number, total: number) => void,
  signal?: AbortSignal,
): Promise<ResultatImportPhoto[]> {
  const resultats: ResultatImportPhoto[] = [];

  for (let index = 0; index < photos.length; index++) {
    const photo = photos[index]!;
    if (signal?.aborted) break; // rien de commencé pour celle-ci : on s'arrête net, pas d'entrée « annulée » en plus

    const enCours: ResultatImportPhoto = { id: photo.id, nom: photo.nom, statut: 'en_cours', date: photo.date };
    surProgres(enCours, index, photos.length);

    try {
      const entete = await deps.lireEntete(photo.fichier);
      let compressee: PhotoCompressee;
      try {
        compressee = await deps.compresser(photo.fichier);
      } catch (err) {
        throw new Error(estProbablementHeic(photo.fichier, entete) ? MESSAGE_HEIC : MESSAGE_LECTURE_IMPOSSIBLE, {
          cause: err,
        });
      }

      let blobId: string;
      try {
        blobId = await deps.televerser(compressee.blob, { signal });
      } catch (err) {
        throw new Error(MESSAGE_ENVOI_ECHEC, { cause: err });
      }

      const aliments: AlimentDetecte[] = [];
      let done: AnalyzeDone;
      try {
        done = await deps.analyser(
          blobId,
          {
            surAliment: (aliment) => aliments.push(aliment),
            surReprise: () => {
              aliments.length = 0; // sortie hors schéma rejouée côté serveur : tout ce qui est reçu avant est caduc
            },
          },
          { signal },
        );
      } catch (err) {
        throw new Error(MESSAGE_ANALYSE_ECHEC, { cause: err });
      }

      const ok: ResultatImportPhoto = { id: photo.id, nom: photo.nom, statut: 'ok', date: photo.date, analyse: done, aliments };
      resultats.push(ok);
      surProgres(ok, index, photos.length);
    } catch (err) {
      const annule = estAbandon(err, signal);
      const echec: ResultatImportPhoto = {
        id: photo.id,
        nom: photo.nom,
        statut: 'echec',
        date: photo.date,
        erreur: annule ? MESSAGE_ANNULE : err instanceof Error ? err.message : MESSAGE_LECTURE_IMPOSSIBLE,
      };
      resultats.push(echec);
      surProgres(echec, index, photos.length);
      if (annule) break; // annulation explicite : on n'entame pas les photos suivantes
    }
  }

  return resultats;
}
